import { supabase } from './supabase';
import type { DBCollection, DBDesign } from './supabase';
import type { Project, Design } from '../types';

export const LAST_COLLECTION_KEY = 'netprobuilder_last_collection';
export const LEGACY_PROJECTS_KEY = 'netprobuilder_designs';
export const LEGACY_LAST_PROJECT_KEY = 'netprobuilder_last_design';

const parseDate = (value: string): Date => new Date(value);

/** UI flags stored in designs.settings (JSON). */
export const designSettingsFromRow = (
  settings: unknown
): Pick<Design, 'equipmentPanelPreviewHidden' | 'logicMode'> => {
  const s = settings && typeof settings === 'object' ? (settings as Record<string, unknown>) : {};
  return {
    equipmentPanelPreviewHidden: s.equipmentPanelPreviewHidden === true,
    logicMode: s.logicMode === 'logic' ? 'logic' : 'plan',
  };
};

export const designSettingsPayload = (design: Design): Record<string, unknown> => ({
  equipmentPanelPreviewHidden: design.equipmentPanelPreviewHidden === true,
  logicMode: design.logicMode === 'logic' ? 'logic' : 'plan',
});

const ensureDesigns = (project: Project): Design[] => {
  if (project.designs && project.designs.length > 0) {
    return project.designs;
  }
  return [
    {
      id: project.id,
      name: project.name,
      createdAt: project.createdAt,
      updatedAt: project.updatedAt,
      racks: project.racks ?? [],
      equipment: project.equipment ?? [],
      cables: project.cables ?? [],
      logicMode: 'plan',
    },
  ];
};

export const collectionToProject = (
  collection: DBCollection,
  designRows: DBDesign[]
): Project => {
  const designs: Design[] = designRows
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((row) => ({
      id: row.legacy_design_id ?? row.id,
      name: row.name,
      createdAt: parseDate(row.created_at),
      updatedAt: parseDate(row.updated_at),
      racks: (row.racks ?? []) as Design['racks'],
      equipment: (row.equipment ?? []) as Design['equipment'],
      cables: (row.cables ?? []) as Design['cables'],
      boxes: (row.boxes ?? undefined) as Design['boxes'],
      labels: (row.labels ?? undefined) as Design['labels'],
      floorPlan: (row.floor_plan ?? undefined) as Design['floorPlan'],
      ...designSettingsFromRow(row.settings),
    }));

  return {
    id: collection.id,
    name: collection.name,
    customer: (collection.customer ?? { name: '', address: '' }) as Project['customer'],
    settings: (collection.settings ?? undefined) as Project['settings'] | undefined,
    racks: [],
    equipment: [],
    cables: [],
    designs,
    createdAt: parseDate(collection.created_at),
    updatedAt: parseDate(collection.updated_at),
    createdBy: collection.created_by,
  };
};

export async function fetchCollectionsForUser(userId: string): Promise<DBCollection[]> {
  const [ownedResult, memberResult] = await Promise.all([
    supabase
      .from('collections')
      .select('*')
      .eq('created_by', userId)
      .order('updated_at', { ascending: false }),
    supabase
      .from('collection_members')
      .select('collections(*)')
      .eq('user_id', userId),
  ]);

  if (ownedResult.error) throw ownedResult.error;

  const byId = new Map<string, DBCollection>();
  for (const row of ownedResult.data ?? []) {
    byId.set(row.id, row as DBCollection);
  }

  if (memberResult.error) {
    console.warn('[fetchCollectionsForUser] shared collections unavailable:', memberResult.error.message);
  } else {
    for (const row of memberResult.data ?? []) {
      const nested = row.collections as DBCollection | DBCollection[] | null;
      const collection = Array.isArray(nested) ? nested[0] : nested;
      if (collection?.id) {
        byId.set(collection.id, collection);
      }
    }
  }

  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );
}

export async function fetchDesignCountsByCollection(
  collectionIds: string[]
): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};
  for (const id of collectionIds) counts[id] = 0;
  if (collectionIds.length === 0) return counts;

  const { data, error } = await supabase
    .from('designs')
    .select('collection_id')
    .in('collection_id', collectionIds);

  if (error) throw error;
  for (const row of data ?? []) {
    const id = (row as { collection_id: string }).collection_id;
    if (!id) continue;
    counts[id] = (counts[id] ?? 0) + 1;
  }
  return counts;
}

export async function fetchDesignsForCollection(collectionId: string): Promise<DBDesign[]> {
  const { data, error } = await supabase
    .from('designs')
    .select('*')
    .eq('collection_id', collectionId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data ?? []) as DBDesign[];
}

export async function loadCollectionAsProject(collectionId: string): Promise<Project | null> {
  const { data: collection, error: collectionError } = await supabase
    .from('collections')
    .select('*')
    .eq('id', collectionId)
    .maybeSingle();

  if (collectionError) throw collectionError;
  if (!collection) return null;

  const designs = await fetchDesignsForCollection(collectionId);
  return collectionToProject(collection as DBCollection, designs);
}

export async function createCollectionRecord(
  userId: string,
  name: string,
  customer: Project['customer'],
  settings: Record<string, unknown> = {}
): Promise<DBCollection> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('collections')
    .insert({
      name,
      customer,
      settings,
      created_by: userId,
      created_at: now,
      updated_at: now,
    })
    .select()
    .single();

  if (error) throw error;
  return data as DBCollection;
}

export async function insertDesignRecord(
  collectionId: string,
  design: Design,
  sortOrder: number
): Promise<DBDesign> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('designs')
    .insert({
      collection_id: collectionId,
      name: design.name,
      racks: design.racks ?? [],
      equipment: design.equipment ?? [],
      cables: design.cables ?? [],
      boxes: design.boxes ?? [],
      labels: design.labels ?? [],
      floor_plan: design.floorPlan ?? null,
      settings: designSettingsPayload(design),
      legacy_design_id: design.id,
      sort_order: sortOrder,
      created_at: now,
      updated_at: now,
    })
    .select()
    .single();

  if (error) throw error;
  return data as DBDesign;
}

export async function saveCollectionProject(
  project: Project,
  userId: string,
  options?: {
    clientWriteId?: string;
    removedDesignIds?: string[];
  }
): Promise<void> {
  const now = new Date().toISOString();
  const designs = ensureDesigns(project);

  const { error: collectionError } = await supabase
    .from('collections')
    .update({
      name: project.name,
      customer: project.customer,
      settings: (project as Project & { settings?: Record<string, unknown> }).settings ?? {},
      updated_at: now,
    })
    .eq('id', project.id)
    .eq('created_by', userId);

  if (collectionError) throw collectionError;

  const existing = await fetchDesignsForCollection(project.id);
  const existingByLegacy = new Map(
    existing.map((row) => [row.legacy_design_id ?? row.id, row])
  );
  const seenDbIds = new Set<string>();
  const collabFields = options?.clientWriteId
    ? { client_write_id: options.clientWriteId, updated_by: userId }
    : {};

  for (let i = 0; i < designs.length; i++) {
    const design = designs[i];
    const match = existingByLegacy.get(design.id);

    if (match) {
      seenDbIds.add(match.id);
      const basePayload = {
        name: design.name,
        racks: design.racks ?? [],
        equipment: design.equipment ?? [],
        cables: design.cables ?? [],
        boxes: design.boxes ?? [],
        labels: design.labels ?? [],
        floor_plan: design.floorPlan ?? null,
        settings: designSettingsPayload(design),
        sort_order: i,
        updated_at: now,
      };
      const { error } = await supabase
        .from('designs')
        .update({ ...basePayload, ...collabFields })
        .eq('id', match.id);

      if (error && options?.clientWriteId) {
        const { error: retryError } = await supabase
          .from('designs')
          .update(basePayload)
          .eq('id', match.id);
        if (retryError) throw retryError;
      } else if (error) {
        throw error;
      }
    } else {
      const inserted = await insertDesignRecord(project.id, design, i);
      seenDbIds.add(inserted.id);
    }
  }

  const removedLegacy = new Set(options?.removedDesignIds ?? []);
  const toDelete = existing.filter((row) => {
    if (seenDbIds.has(row.id)) return false;
    if (options?.removedDesignIds) {
      const legacyId = row.legacy_design_id ?? row.id;
      return removedLegacy.has(legacyId);
    }
    return true;
  });
  if (toDelete.length > 0) {
    const { error } = await supabase
      .from('designs')
      .delete()
      .in('id', toDelete.map((row) => row.id));

    if (error) throw error;
  }
}

export async function deleteCollectionRecord(collectionId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('collections')
    .delete()
    .eq('id', collectionId)
    .eq('created_by', userId);

  if (error) throw error;
}

export async function renameCollectionRecord(
  collectionId: string,
  userId: string,
  name: string
): Promise<void> {
  const { error } = await supabase
    .from('collections')
    .update({ name, updated_at: new Date().toISOString() })
    .eq('id', collectionId)
    .eq('created_by', userId);

  if (error) throw error;
}

/** Consolidate legacy sites into a collection (SECURITY DEFINER RPC — bypasses RLS). */
export async function consolidateSitesToCollection(collectionId: string): Promise<number> {
  const { data, error } = await supabase.rpc('consolidate_sites_to_collection', {
    p_collection_id: collectionId,
  });

  if (error) {
    console.warn('[consolidateSitesToCollection]', error);
    return 0;
  }

  return typeof data === 'number' ? data : 0;
}

/** Link orphaned sites and unscoped stock rows to a collection. */
export async function linkOrphanedDataToCollection(collectionId: string): Promise<void> {
  await consolidateSitesToCollection(collectionId);
  await claimUnscopedStockToCollection(collectionId);
}

/** Assign stock catalogue rows that have no collection yet. */
export async function claimUnscopedStockToCollection(collectionId: string): Promise<void> {
  await supabase
    .from('items')
    .update({ collection_id: collectionId })
    .is('collection_id', null);

  await supabase
    .from('warehouses')
    .update({ collection_id: collectionId })
    .is('collection_id', null);

  await supabase
    .from('owners')
    .update({ collection_id: collectionId })
    .is('collection_id', null);
}

export async function importLegacyLocalStorage(userId: string): Promise<string | null> {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(LEGACY_PROJECTS_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  const entries = Object.values(parsed ?? {}) as Project[];
  if (entries.length === 0) return null;

  let firstCollectionId: string | null = null;
  const legacyIdToCollectionId = new Map<string, string>();

  for (const entry of entries) {
    const customer = entry.customer ?? { name: '', address: '' };
    const settings = (entry as Project & { settings?: Record<string, unknown> }).settings ?? {};

    const collection = await createCollectionRecord(
      userId,
      entry.name || 'Imported Collection',
      customer,
      settings
    );

    legacyIdToCollectionId.set(entry.id, collection.id);
    if (!firstCollectionId) firstCollectionId = collection.id;

    const designs = ensureDesigns({
      ...entry,
      createdAt: entry.createdAt ? new Date(entry.createdAt as unknown as string) : new Date(),
      updatedAt: entry.updatedAt ? new Date(entry.updatedAt as unknown as string) : new Date(),
    });

    for (let i = 0; i < designs.length; i++) {
      await insertDesignRecord(collection.id, designs[i], i);
    }

    await linkOrphanedDataToCollection(collection.id);
  }

  const lastLegacy = localStorage.getItem(LEGACY_LAST_PROJECT_KEY);
  if (lastLegacy && legacyIdToCollectionId.has(lastLegacy)) {
    return legacyIdToCollectionId.get(lastLegacy)!;
  }

  return firstCollectionId;
}

export function getLastCollectionId(): string | null {
  try {
    return localStorage.getItem(LAST_COLLECTION_KEY);
  } catch {
    return null;
  }
}

export function setLastCollectionId(collectionId: string): void {
  try {
    localStorage.setItem(LAST_COLLECTION_KEY, collectionId);
  } catch {
    // ignore
  }
}

export function clearLastCollectionId(): void {
  try {
    localStorage.removeItem(LAST_COLLECTION_KEY);
  } catch {
    // ignore
  }
}
